import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Alert } from '@/types/shelter';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useRef, useCallback } from 'react';
import { useOffline } from '@/contexts/OfflineContext';
import { getFromOfflineStore } from '@/lib/offlineDb';

export const useAlerts = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isOnline, queueOfflineMutation, cacheData } = useOffline();

  // Debounced realtime invalidation — batch concurrent updates from many staff
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedInvalidate = useCallback((keys: string[][]) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      keys.forEach(key => queryClient.invalidateQueries({ queryKey: key }));
      debounceRef.current = null;
    }, 500);
  }, [queryClient]);

  // Set up real-time subscription
  useEffect(() => {
    if (!isOnline) return;
    
    console.log('Setting up alerts realtime subscription');
    
    const channel = supabase
      .channel('alerts-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'alerts'
        },
        (payload) => {
          console.log('New alert inserted:', payload);
          debouncedInvalidate([['alerts']]);
          
          const newAlert = payload.new as any;
          toast({
            title: 'New Alert',
            description: `${newAlert.type.toUpperCase()}: ${newAlert.title}`,
            variant: newAlert.type === 'critical' ? 'destructive' : 'default'
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'alerts'
        },
        (payload) => {
          console.log('Alert updated:', payload);
          debouncedInvalidate([['alerts'], ['clients']]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'alerts'
        },
        (payload) => {
          console.log('Alert deleted:', payload);
          debouncedInvalidate([['alerts']]);
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up alerts realtime subscription');
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [queryClient, toast, isOnline, debouncedInvalidate]);

  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ['alerts'],
    queryFn: async () => {
      if (!isOnline) {
        console.log('Offline: Loading alerts from cache');
        return getFromOfflineStore<Alert>('alerts');
      }

      const { data, error } = await supabase
        .from('alerts')
        .select(`
          *,
          acknowledgements:alert_acknowledgements(
            id,
            user_id,
            acknowledged_at
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedAlerts = data.map(alert => ({
        id: alert.id,
        type: alert.type as Alert['type'],
        title: alert.title,
        description: alert.description || '',
        timestamp: alert.created_at || new Date().toISOString(),
        status: alert.status as Alert['status'],
        createdBy: alert.created_by || undefined,
        acknowledgedBy: alert.acknowledged_by || undefined,
        clientId: alert.client_id || undefined,
        site_id: alert.site_id || undefined,
        category: alert.category as Alert['category'],
        acknowledgements: alert.acknowledgements?.map((ack: any) => ({
          id: ack.id,
          alertId: alert.id,
          userId: ack.user_id,
          acknowledgedAt: ack.acknowledged_at,
          userName: undefined
        })) || []
      })) as (Alert & { site_id?: string })[];

      // Cache data for offline use
      await cacheData('alerts', transformedAlerts);
      
      return transformedAlerts;
    },
    staleTime: isOnline ? 30000 : Infinity
  });

  const createAlert = useMutation({
    mutationFn: async (alertData: Omit<Alert, 'id' | 'timestamp' | 'status'> & { status?: Alert['status']; siteId?: string }) => {
      const id = crypto.randomUUID();
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get user's first site if no siteId provided
      let alertSiteId = alertData.siteId;
      if (!alertSiteId && user) {
        const { data: userSites } = await supabase
          .from('user_sites')
          .select('site_id')
          .eq('user_id', user.id)
          .limit(1);
        alertSiteId = userSites?.[0]?.site_id || null;
      }
      
      const alertEntry = {
        id,
        type: alertData.type,
        title: alertData.title,
        description: alertData.description,
        category: alertData.category,
        status: alertData.status || 'active',
        client_id: alertData.clientId,
        site_id: alertSiteId || null,
        created_at: new Date().toISOString(),
        created_by: user?.id
      };

      if (!isOnline) {
        await queueOfflineMutation('alerts', 'insert', alertEntry);
        toast({
          title: 'Saved Offline',
          description: 'Alert will sync when back online',
        });
        return alertEntry;
      }

      const { data, error } = await supabase
        .from('alerts')
        .insert([alertEntry])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      if (isOnline) {
        toast({
          title: 'Success',
          description: 'Alert created successfully'
        });
      }
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to create alert'
      });
    }
  });

  const updateAlert = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Alert> & { id: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const updateData: any = {};
      if (updates.status) updateData.status = updates.status;
      if (updates.title) updateData.title = updates.title;
      if (updates.status === 'resolved') updateData.acknowledged_by = user?.id;
      
      const { data, error } = await supabase
        .from('alerts')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      toast({
        title: 'Success',
        description: 'Alert updated successfully'
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to update alert'
      });
    }
  });

  return {
    alerts,
    isLoading,
    createAlert: createAlert.mutate,
    updateAlert: updateAlert.mutate
  };
};
